import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useShopOverrideRules from "@babackapp/private-api-client/client/use-shop-override-rules";

/** A setting that can ask the customer to pay through Stripe in the portal. */
export type CustomerPaymentSource =
  | "exchange-credit"
  | "reshipping"
  | "reshipping-rule";

type ShopForCustomerPayments = {
  plan: { exchangeWithOtherArticles?: boolean | null };
  exchange: {
    activated?: boolean | null;
    exchangeWithOtherArticles?: boolean | null;
    reshippingHasToBePaid?: boolean | null;
  };
};

type OverrideRuleForCustomerPayments = {
  isActive: boolean;
  action: { type: string; parameters?: unknown };
};

/**
 * BBS-386: the settings under which a customer may pay a complement. Without
 * a connected Stripe account, create-intent refuses these payments; before,
 * they were collected on Baback's platform account, never on the merchant's.
 */
export function getCustomerPaymentSources(
  shop: ShopForCustomerPayments,
  overrideRules: OverrideRuleForCustomerPayments[] = [],
): CustomerPaymentSource[] {
  if (!shop.exchange.activated) return [];

  const sources: CustomerPaymentSource[] = [];
  if (
    shop.plan.exchangeWithOtherArticles &&
    shop.exchange.exchangeWithOtherArticles
  )
    sources.push("exchange-credit");
  if (shop.exchange.reshippingHasToBePaid) sources.push("reshipping");
  const hasPaidReshippingRule = overrideRules.some(
    (rule) =>
      rule.isActive &&
      rule.action.type === "OVERRIDE_RESHIPPING_PAID" &&
      (rule.action.parameters as { value?: unknown } | null)?.value === true,
  );
  if (hasPaidReshippingRule) sources.push("reshipping-rule");
  return sources;
}

/**
 * `userId` is what create-intent sends to Stripe, not `activated`; an account
 * whose onboarding is unfinished (`details_submitted`) cannot take charges.
 */
export function isStripeReadyForPayments(shop: {
  integrations: {
    stripe?: {
      userId?: string | null;
      details_submitted?: boolean | null;
    } | null;
  };
}): boolean {
  const stripe = shop.integrations.stripe;
  return !!stripe?.userId && stripe.details_submitted === true;
}

/**
 * The customer payments the current shop's settings allow but cannot collect,
 * for want of a Stripe account ready for payments. Empty when nothing is
 * wrong. Override rules are only readable by owners and admins
 * (`ShopMemberRoles`): pass `canReadOverrideRules` false for anyone else, the
 * rules are then left out instead of calling the API for a 403.
 */
export function useUncollectableCustomerPayments({
  canReadOverrideRules,
}: {
  canReadOverrideRules: boolean;
}): CustomerPaymentSource[] {
  const { data: shop } = useCurrentShopSuspense();
  const isReady = isStripeReadyForPayments(shop);
  const { data: overrideRules = [] } = useShopOverrideRules(
    canReadOverrideRules && !isReady ? shop.id : null,
  );
  if (isReady) return [];
  return getCustomerPaymentSources(shop, overrideRules);
}
